import React from 'react';
import { Button } from 'lib/button';
import { focusOutlineColors } from 'lib/core';
import { NestedList } from 'lib/list';
import { withTooltip } from 'lib/tooltip';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { customNavScrollbar } from '../shared-styles';

const StyledCollapsedWrapper = styled.div`
  ${customNavScrollbar};
  display: flex;
  flex-direction: column;
  padding: 4px 0 0 0;
  width: 52px;
  svg {
    z-index: unset;
  }
`;

const StyledIconButton = styled(Button)`
  &&& svg {
    fill: ${({ theme }) => theme.color.text.light.secondary.value};
  }
  margin: 4px auto;
  &&:hover,
  &&:focus {
    background-color: ${({ theme }) => theme.color.gray[200].value};
    &&& svg {
      fill: ${({ theme }) => theme.color.text.light.secondary.value};
    }
  }
  &&:focus-visible {
    box-shadow: inset 0 0 0 4px ${({ theme }) => focusOutlineColors.getDark(theme)};
  }
  &&:active {
    background-color: ${({ theme }) => theme.color.gray[300].value};
  }
`;

const StyledIconWithToolTipContainer = styled.div`
  margin: 0 auto;
`;

export const NavListItemsCollapsed = ({
  className,
  dataTestId = undefined,
  expandedItemIds = {},
  items,
  onExpand,
  onItemExpandCollapse,
}) => {
  const getIdFromEvent = (node) => {
    if (node.id) {
      return node.id;
    } else {
      if (node.parentNode) {
        return getIdFromEvent(node.parentNode);
      }
    }
  };

  const getSingleItem = (id) => {
    const singleItem = items.find((item) => item.id === id);
    return singleItem;
  };

  const handleExpand = (event) => {
    /* this code sets the activeItemId if there are no List Items to expand,
       OR expands the list items on button click */
    const foundId = getIdFromEvent(event.target);
    const foundItem = getSingleItem(foundId);
    if (foundItem.onClick) {
      foundItem.onClick();
    } else if (foundItem.items) {
      const isItemExpanded = expandedItemIds[foundItem.id];
      const nextExpandedItemIds = { ...expandedItemIds };
      if (!isItemExpanded) {
        nextExpandedItemIds[foundItem.id] = true;
      }
      onItemExpandCollapse(nextExpandedItemIds);
    }
    onExpand();
  };

  return (
    <StyledCollapsedWrapper>
      {items &&
        items.map((item, i) => {
          if (item.icon) {
            if (item.tooltip) {
              const navButtonTooltipProps = item.tooltip.tooltipProps;
              const HelpRenderTooltipButton = (props) => {
                return (
                  <StyledIconButton
                    className={`${className}-list-item-button`}
                    dataTestId={dataTestId ? `${dataTestId}-main-menu-item-button-${item.id}` : undefined}
                    hideText
                    icon={item.icon}
                    id={item.id}
                    onClick={handleExpand}
                    text=""
                    variant="text"
                    {...props}
                  />
                );
              };
              const StyledIconButtonWithTooltip = withTooltip(HelpRenderTooltipButton, navButtonTooltipProps);
              return (
                <StyledIconWithToolTipContainer key={`container-${i}`}>
                  <StyledIconButtonWithTooltip key={`icon-${i}`} {...item.tooltip.positioningProps} />
                </StyledIconWithToolTipContainer>
              );
            } else {
              return (
                <StyledIconButton
                  className={`${className}-list-item-button`}
                  dataTestId={dataTestId ? `${dataTestId}-main-menu-item-button-${item.id}` : undefined}
                  hideText
                  icon={item.icon}
                  id={item.id}
                  key={`icon-${i}`}
                  onClick={handleExpand}
                  text=""
                  variant="text"
                />
              );
            }
          } else {
            return null;
          }
        })}
    </StyledCollapsedWrapper>
  );
};

NavListItemsCollapsed.propTypes = {
  /** Adds additional class to the component */
  className: PropTypes.string.isRequired,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Item ids that are currently expanded. This prop consists of object entries, where key is the id of the item
   * and value is true if item is expanded and falsy (false, null, undefined) if it is not */
  expandedItemIds: PropTypes.shape({}),
  /** Items of navigation menu */
  items: PropTypes.arrayOf(PropTypes.shape({ ...NestedList.propTypes.items })).isRequired,
  /** Callback that is called on item expand */
  onExpand: PropTypes.func.isRequired,
  /** Callback that is called on item expand or collapse */
  onItemExpandCollapse: PropTypes.func.isRequired,
};
